import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { LIFECYCLE_FEEDS, lifecycleState, signalsFrom, stageGates, nextStep } from '../data/lifecycle';
import { MODULES } from '../data/consoleCatalog';
import { useEntityList } from '../data/entities';
import { useOperate } from './AppOperate';

// The app's life as one control: three stages (Lab → Ready to ship →
// Operate), a check where the work has gotten, a highlight where you are,
// a greyed stage that is not ready yet with its blocker as the tooltip
// (guided gates — visible, named, never a hard lock), the ONE next step as
// a button, and a return to Lab. On App Builder it is the navigation
// (variant="tabs"). On a library page it links back to the studio and
// highlights the stage that page feeds.

const stageHref = (orgId, app, id, extra = null) => {
  const params = new URLSearchParams();
  if (app?.id) params.set('app', app.id);
  if (id && id !== 'lab') params.set('tab', id);
  if (extra) for (const [k, v] of Object.entries(extra)) if (v) params.set(k, String(v));
  const q = params.toString();
  return `/orgs/${orgId}/builder${q ? `?${q}` : ''}`;
};

export function useLifecycleSignals(org, app) {
  const ops = useOperate(org?.id, app?.id);
  const { docs: integrations } = useEntityList('integration', { org: org?.id }, { enabled: !!org?.id && !!app });
  const known = integrations != null;
  const integs = known && app ? integrations.filter((d) => (d.appIds || []).includes(app.id)) : undefined;
  return signalsFrom({
    app,
    org,
    operate: ops.data,
    integrations: known ? (integs || []) : undefined,
  });
}

export function LibraryLifecycle({ org, app, moduleId }) {
  const signals = useLifecycleSignals(org, app);
  const here = LIFECYCLE_FEEDS[moduleId];
  if (!app || !here) return null;
  const feed = MODULES.find((m) => m.to === moduleId)?.label || moduleId;
  return (
    <LifecycleRail variant="links" orgId={org.id} app={app} here={here} signals={signals} next={nextStep({ app, signals })} compact feed={feed} />
  );
}

function Node({ n, variant, href, onSelect, onKeyDown, count, nodeRef }) {
  const cls = `life-node${n.here ? ' is-here' : ''}${n.reached ? ' is-reached' : ''}${n.disabled ? ' is-blocked' : ''}`;
  const title = n.disabled ? `Not yet — ${n.blocker}` : n.purpose;
  const inner = (
    <>
      <span className="life-mark" aria-hidden="true">{n.reached ? '✓' : n.disabled ? '·' : ''}</span>
      <span className="life-label">{n.label}</span>
      {count != null && <span className="life-count">{count}</span>}
    </>
  );
  if (variant === 'tabs') {
    return (
      <button
        type="button"
        role="tab"
        id={`life-tab-${n.id}`}
        className={cls}
        aria-selected={n.here}
        aria-disabled={n.disabled || undefined}
        tabIndex={n.here ? 0 : -1}
        title={title}
        onClick={() => { if (!n.disabled) onSelect(n.id); }}
        onKeyDown={(e) => onKeyDown(e, n.id)}
        ref={nodeRef}
      >
        {inner}
      </button>
    );
  }
  if (n.disabled) {
    return <span className={cls} title={title} aria-disabled="true" ref={nodeRef}>{inner}</span>;
  }
  return (
    <Link to={href} className={cls} aria-current={n.here ? 'step' : undefined} title={title} ref={nodeRef}>
      {inner}
    </Link>
  );
}

export default function LifecycleRail({
  variant = 'links',
  here = null,
  onSelect = null,
  signals = null,
  counts = null,
  orgId = null,
  app = null,
  compact = false,
  feed = null,
  caption = null,
  brief = null,
  next = null,
}) {
  const nodes = lifecycleState({ here, signals, gates: stageGates(signals || {}) });
  const open = nodes.find((n) => !n.reached);
  const shown = nodes.find((n) => n.here) || open || nodes[nodes.length - 1];
  const pending = !nodes.some((n) => n.here) && !!open;
  const currentRef = useRef(null);
  const moveFocus = useRef(false);

  useEffect(() => {
    const el = currentRef.current;
    if (!el) return;
    el.scrollIntoView({ inline: 'nearest', block: 'nearest' });
    if (moveFocus.current) {
      el.focus();
      moveFocus.current = false;
    }
  }, [here]);

  const onKeyDown = (e, id) => {
    if (variant !== 'tabs' || !onSelect) return;
    const order = nodes.filter((n) => !n.disabled).map((n) => n.id);
    const i = order.indexOf(id);
    let to = null;
    if (e.key === 'ArrowRight') to = order[(i + 1) % order.length];
    else if (e.key === 'ArrowLeft') to = order[(i - 1 + order.length) % order.length];
    else if (e.key === 'Home') to = order[0];
    else if (e.key === 'End') to = order[order.length - 1];
    if (!to || to === id) return;
    e.preventDefault();
    moveFocus.current = true;
    onSelect(to);
  };

  const loopLabel = 'Next change starts in the Lab';
  const loop = variant === 'tabs'
    ? <button type="button" className="life-return" aria-label={loopLabel} onClick={() => onSelect && onSelect('lab')}>↺ next change</button>
    : <Link className="life-return" aria-label={loopLabel} to={stageHref(orgId, app, 'lab')}>↺ next change</Link>;

  return (
    <div className={`life-rail${compact ? ' is-compact' : ''}`}>
      <div className="life-rail-scroll">
        <div className="life-rail-track">
          <div
            className="life-rail-tabs"
            role={variant === 'tabs' ? 'tablist' : undefined}
            aria-label="App lifecycle"
          >
            {nodes.map((n) => (
              <Node
                key={n.id}
                n={n}
                variant={variant}
                href={stageHref(orgId, app, n.id)}
                onSelect={onSelect}
                onKeyDown={onKeyDown}
                count={counts ? counts[n.id] : null}
                nodeRef={n.here ? currentRef : null}
              />
            ))}
          </div>
          <div className="life-rail-return">
            <span className="life-rail-return-arm" aria-hidden="true" />
            {loop}
          </div>
        </div>
        {next && <NextButton next={next} variant={variant} onSelect={onSelect} orgId={orgId} app={app} />}
      </div>
      {feed && shown && (
        <p className="life-rail-feed">{feed} feeds {shown.label}{app?.name ? ` for ${app.name}` : ''}.</p>
      )}
      {caption && <p className="life-rail-feed">{caption}</p>}
      {shown && (
        <p className="life-rail-purpose">
          {pending && !next && <span className="life-rail-k">Next · </span>}
          <span className="life-rail-k">{shown.label}.</span> {shown.purpose}
          {shown.disabled && shown.blocker && <span className="life-rail-blocker"> Not yet — {shown.blocker}.</span>}
        </p>
      )}
      {variant === 'tabs' && brief && <StageBrief brief={brief} onSelect={onSelect} />}
    </div>
  );
}

// The ONE next step, always present (lifecycle.js nextStep): a stage, a
// Lab phase when it is in the Lab, and a label that fits a button.
function NextButton({ next, variant, onSelect, orgId, app }) {
  const extra = next.phase ? { phase: next.phase, card: next.card || null, roadmap: next.roadmap || null } : null;
  const title = next.text || next.label;
  if (next.href && /^https?:/.test(next.href)) {
    return <a className="cs-btn cs-btn-primary cs-btn-sm life-next" href={next.href} target="_blank" rel="noreferrer" title={title}>Next: {next.label} ↗</a>;
  }
  if (variant === 'tabs' && onSelect) {
    return <button type="button" className="cs-btn cs-btn-primary cs-btn-sm life-next" title={title} onClick={() => onSelect(next.stage, extra)}>Next: {next.label} →</button>;
  }
  return <Link className="cs-btn cs-btn-primary cs-btn-sm life-next" title={title} to={stageHref(orgId, app, next.stage, extra)}>Next: {next.label} →</Link>;
}

function StageBrief({ brief, onSelect }) {
  const a = brief.attention;
  const go = () => {
    if (a?.tab && onSelect) onSelect(a.tab, a.extra || null);
    if (a?.anchor) requestAnimationFrame(() => document.getElementById(a.anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };
  return (
    <div className="life-brief">
      <p className="life-brief-line"><span className="life-brief-k">This decision.</span> {brief.decision}</p>
      {brief.decided?.length > 0 && (
        <p className="life-brief-line"><span className="life-brief-k">Already decided.</span> {brief.decided.join(' · ')}</p>
      )}
      {a?.text && (
        <p className="life-brief-line life-brief-need">
          <span><span className="life-brief-k">Needs you.</span> {a.text}</span>
          {a.href
            ? <Link to={a.href} className="cs-btn cs-btn-primary cs-btn-sm">{a.label || 'Open'}</Link>
            : (a.tab && onSelect) && <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={go}>{a.label || 'Open'}</button>}
        </p>
      )}
    </div>
  );
}
